import React from 'react';
import { Formik, FormikProps } from 'formik';
import { Form } from 'formik-antd';
import * as Yup from 'yup';

import FormikEffect from 'components/formik/FormikEffect';
import WorkerSection, {
  WorkerFormValues,
  workerInitialValidationSchema,
  workerInitialValues,
} from 'components/modals/AssignmentDataModal/WorkerSection';
import CompanySection, {
  CompanyFormValues,
  companyInitialValidationSchema,
  companyInitialValues,
} from 'components/modals/AssignmentDataModal/CompanySection';
import {
  AssignmentTypeEnum,
  ContractorCompanyModel,
  ResourceDownloadResponse,
  WorkerDetailViewModel,
} from 'api/backofficeApiClient';
import { checkIsCountriesWithStatesDropdown } from 'utils/validators/countryUtils';
import { Address, Addresses, FormType } from '../../AssignmentModal';

type Props = {
  formRef: any;
  companyId?: string;
  workers: WorkerDetailViewModel[];
  companies: ContractorCompanyModel[];
  assignmentType?: AssignmentTypeEnum;
  onChange: (formik: FormikProps<WorkerInfoFormValues>) => void;
  initialValues?: WorkerInfoFormValues;
  fileInfo?: ResourceDownloadResponse;
  isCompaniesLoaded: boolean;
  isWorkersLoaded: boolean;
  isRenewOrEditRenewMode: boolean;
  isRehireOrEditRehireMode: boolean;
  hasActiveAssignmentRestriction: boolean;
  isZipRequired: boolean;
  addresses: Addresses;
  onAddressChange: (
    formType: FormType,
    field: keyof Address,
    value: string | undefined,
  ) => void;
};

export interface WorkerInfoFormValues {
  company: CompanyFormValues;
  worker: WorkerFormValues;
  taxId: number | null;
  workerFileKey: string[];
}

const initialValidationSchema = {
  worker: workerInitialValidationSchema,
  company: companyInitialValidationSchema,
  taxId: Yup.number().nullable(),
  workerFileKey: Yup.array().of(Yup.string()),
};

const getSubmitValidationSchema = (
  addresses: Addresses,
  isZipRequired: boolean,
) =>
  Yup.object({
    ...initialValidationSchema,
    company: Yup.object({
      ...initialValidationSchema.company,
      companyId:
        initialValidationSchema.company.companyId.required('Field is required'),
      companyName:
        initialValidationSchema.company.companyName.required(
          'Field is required',
        ),
      firstName:
        initialValidationSchema.company.firstName.required('Field is required'),
      lastName:
        initialValidationSchema.company.lastName.required('Field is required'),
      email:
        initialValidationSchema.company.email.required('Field is required'),
      phone:
        initialValidationSchema.company.phone.required('Field is required'),
      country:
        initialValidationSchema.company.country.required('Field is required'),
      state: initialValidationSchema.company.state.when('country', {
        is: (country: number) => checkIsCountriesWithStatesDropdown(country),
        then: Yup.string().required('Field is required'),
      }),
      city: initialValidationSchema.company.city.when('country', {
        is: (country: number) => checkIsCountriesWithStatesDropdown(country),
        then: Yup.string().required('Field is required'),
      }),
      internationalStateName:
        initialValidationSchema.company.internationalStateName.when('country', {
          is: (country: number) => !checkIsCountriesWithStatesDropdown(country),
          then: Yup.string().required('Field is required'),
        }),
      internationalCityName:
        initialValidationSchema.company.internationalCityName.when('country', {
          is: (country: number) => !checkIsCountriesWithStatesDropdown(country),
          then: Yup.string().required('Field is required'),
        }),
      address:
        initialValidationSchema.company.address.required('Field is required'),
      zip: isZipRequired
        ? initialValidationSchema.company.zip
            .trim()
            .required('Zip Code is required')
        : initialValidationSchema.company.zip.trim(),
    }),
    worker: Yup.object({
      ...initialValidationSchema.worker,
      workerId:
        initialValidationSchema.worker.workerId.required('Field is required'),
      firstName:
        initialValidationSchema.worker.firstName.required('Field is required'),
      lastName:
        initialValidationSchema.worker.lastName.required('Field is required'),
      email: initialValidationSchema.worker.email.required('Field is required'),
      workerType:
        initialValidationSchema.worker.workerType.required('Field is required'),
      phone: initialValidationSchema.worker.phone.required('Field is required'),
      country:
        initialValidationSchema.worker.country.required('Field is required'),
      state: initialValidationSchema.worker.state.when('country', {
        is: (country: number) => checkIsCountriesWithStatesDropdown(country),
        then: Yup.string().required('Field is required'),
      }),
      city: initialValidationSchema.worker.city.when('country', {
        is: (country: number) => checkIsCountriesWithStatesDropdown(country),
        then: Yup.string().required('Field is required'),
      }),
      internationalStateName:
        initialValidationSchema.worker.internationalStateName.when('country', {
          is: (country: number) => !checkIsCountriesWithStatesDropdown(country),
          then: Yup.string().required('Field is required'),
        }),
      internationalCityName:
        initialValidationSchema.worker.internationalCityName.when('country', {
          is: (country: number) => !checkIsCountriesWithStatesDropdown(country),
          then: Yup.string().required('Field is required'),
        }),
      address: initialValidationSchema.worker.address
        .required('Field is required')
        .billingAndWorkerAddressSameCheck(
          addresses.worker.address,
          addresses.billing.address,
        ),
      zip: isZipRequired
        ? initialValidationSchema.worker.zip
            .trim()
            .required('Zip Code is required')
        : initialValidationSchema.worker.zip.trim(),
    }),
  });

export default React.memo((props: Props) => {
  const {
    formRef,
    onChange,
    assignmentType,
    initialValues: predefinedInitialValues,
    workers,
    companies,
    fileInfo,
    isCompaniesLoaded,
    isWorkersLoaded,
    isRenewOrEditRenewMode,
    isRehireOrEditRehireMode,
    hasActiveAssignmentRestriction,
    isZipRequired,
    addresses,
    onAddressChange,
  } = props;
  const initialValues: WorkerInfoFormValues = {
    worker: workerInitialValues,
    company: companyInitialValues,
    taxId: null,
    workerFileKey: [],
  };

  return (
    <Formik
      innerRef={formRef}
      enableReinitialize
      initialValues={predefinedInitialValues || initialValues}
      validationSchema={getSubmitValidationSchema(addresses, isZipRequired)}
      onSubmit={() => {}}
    >
      {(formik) => (
        <>
          <FormikEffect onChange={onChange} />

          <Form layout="vertical">
            <div className="modal-scroll-wrapper">
              <CompanySection
                formik={formik}
                companies={companies}
                isCompaniesLoaded={isCompaniesLoaded}
                assignmentType={assignmentType}
                contractorCompanyFileInfo={fileInfo}
                predefinedInitialValues={predefinedInitialValues}
                isRenewOrEditRenewMode={isRenewOrEditRenewMode}
                isRehireOrEditRehireMode={isRehireOrEditRehireMode}
                hasActiveAssignmentRestriction={hasActiveAssignmentRestriction}
                isZipRequired={isZipRequired}
              />

              <WorkerSection<WorkerInfoFormValues>
                workers={workers}
                isWorkersLoaded={isWorkersLoaded}
                formik={formik}
                predefinedInitialValues={predefinedInitialValues}
                isRenewOrEditRenewMode={isRenewOrEditRenewMode}
                isRehireOrEditRehireMode={isRehireOrEditRehireMode}
                hasActiveAssignmentRestriction={hasActiveAssignmentRestriction}
                isZipRequired={isZipRequired}
                addresses={addresses}
                onAddressChange={onAddressChange}
              />
            </div>
          </Form>
        </>
      )}
    </Formik>
  );
});
